import { ChevronDown, SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { SkillFilterState } from "@/hooks/use-skill-filters";
import type { FacetCounts } from "@/lib/skills/catalog";
import { RISK } from "@/lib/skills/constants";
import type { DiscoverySearch } from "@/lib/skills/search-params";
import type { RiskLevel } from "@/lib/skills/types";
import { cn } from "@/lib/utils";

const RISK_ORDER: RiskLevel[] = ["vetted", "low", "review", "high"];

/** Shared filter groups (Risk / Rating / Freshness / Author). Lives in the popover. */
function FilterGroups({
  state,
  counts,
  update,
}: {
  state: SkillFilterState;
  counts: FacetCounts;
  update: (patch: Partial<DiscoverySearch>) => void;
}) {
  function toggleRisk(r: RiskLevel) {
    const next = state.risks.includes(r) ? state.risks.filter((x) => x !== r) : [...state.risks, r];
    update({ risk: next.length ? next : undefined });
  }
  function toggleAuthor(id: string) {
    const next = state.authors.includes(id)
      ? state.authors.filter((x) => x !== id)
      : [...state.authors, id];
    update({ authors: next.length ? next : undefined });
  }

  return (
    <div className="flex flex-col gap-5">
      <FilterSection title="Risk level">
        {RISK_ORDER.map((r) => (
          <label
            key={r}
            htmlFor={`risk-filter-${r}`}
            className="flex cursor-pointer items-center gap-2 text-sm text-fg-2"
          >
            <Checkbox
              id={`risk-filter-${r}`}
              checked={state.risks.includes(r)}
              onCheckedChange={() => toggleRisk(r)}
            />
            <span className="flex-1">{RISK[r].label}</span>
            <span className="text-xs text-fg-3">{counts.risk[r]}</span>
          </label>
        ))}
      </FilterSection>

      <FilterSection title="Minimum rating">
        <ToggleGroup
          type="single"
          value={String(state.minRating)}
          onValueChange={(v) => update({ minRating: v ? Number(v) || undefined : undefined })}
        >
          <ToggleGroupItem value="0">Any</ToggleGroupItem>
          <ToggleGroupItem value="4">4★+</ToggleGroupItem>
          <ToggleGroupItem value="4.5">4.5★+</ToggleGroupItem>
        </ToggleGroup>
      </FilterSection>

      <FilterSection title="Freshness">
        <ToggleGroup
          type="single"
          value={String(state.freshness)}
          onValueChange={(v) => update({ freshness: v ? Number(v) || undefined : undefined })}
        >
          <ToggleGroupItem value="0">Any</ToggleGroupItem>
          <ToggleGroupItem value="90">90 days</ToggleGroupItem>
          <ToggleGroupItem value="30">30 days</ToggleGroupItem>
        </ToggleGroup>
      </FilterSection>

      <FilterSection title="Author">
        {counts.authors.map((a) => (
          <label
            key={a.id}
            htmlFor={`author-filter-${a.id}`}
            className="flex cursor-pointer items-center gap-2 text-sm text-fg-2"
          >
            <Checkbox
              id={`author-filter-${a.id}`}
              checked={state.authors.includes(a.id)}
              onCheckedChange={() => toggleAuthor(a.id)}
            />
            <span className="flex-1 truncate">{a.name}</span>
            <span className="text-xs text-fg-3">{a.count}</span>
          </label>
        ))}
      </FilterSection>
    </div>
  );
}

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-fg-3">
        {title}
      </h4>
      <div className="flex flex-col gap-2">{children}</div>
    </div>
  );
}

export function FilterPopover({
  state,
  counts,
  update,
  activeCount,
  onClearAll,
}: {
  state: SkillFilterState;
  counts: FacetCounts;
  update: (patch: Partial<DiscoverySearch>) => void;
  activeCount: number;
  onClearAll: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-9 items-center gap-2 rounded-[9px] border border-border-1 bg-white px-3 text-sm font-medium text-fg-2 transition-colors hover:bg-bg-3",
            activeCount > 0 && "border-fg-link text-fg-link",
          )}
        >
          <SlidersHorizontal className="size-4" />
          <span>Filters</span>
          {activeCount > 0 && (
            <span className="grid size-5 place-items-center rounded-full bg-fg-link text-[11px] font-semibold text-white">
              {activeCount}
            </span>
          )}
          <ChevronDown className="size-3.5 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between border-b border-border-1 px-4 py-3">
          <span className="text-sm font-semibold text-fg-1">Refine results</span>
          {activeCount > 0 && (
            <button
              type="button"
              onClick={onClearAll}
              className="text-xs font-medium text-fg-link hover:underline"
            >
              Clear all
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-4 py-4">
          <FilterGroups state={state} counts={counts} update={update} />
        </div>
        <div className="border-t border-border-1 px-4 py-3">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="h-9 w-full rounded-[9px] bg-navy text-sm font-semibold text-white transition-colors hover:bg-navy-deep"
          >
            Show results
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
